import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Table as ReactstrapTable } from 'reactstrap';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import Spinner from '~components/Spinner';

import TableEmpty from './TableEmpty';
import TableFooter from './TableFooter';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import TableRowNew from './TableRowNew';
import { InternalActions, TableActions, TableViewRow, ValidationResultMap } from './types';
import { useTableHandlers } from './useTableHandlers';
import { useTableViewState } from './useTableViewState';
import { resolveRowAction, setIfValidRow, validateRowAndSetResult } from './utils';

interface ComponentProps {
  id: string;
  data: TableViewRow[];
  columns: ColumnDef<any>[];
  actions?: TableActions;

  isLoading?: boolean;
  allowSort?: boolean;
  allowPagination?: boolean;
  manualSort?: boolean;
  initialSortState?: SortingState;
  onSort?: (params: SortingState) => void;
  onLoadMore?: () => void;

  showHeader?: boolean;
  showCount?: boolean;
  totalCount?: number;

  getRowId?: (row: TableViewRow) => string;
  getRowById?: (id: string | number) => TableViewRow | undefined;
  getDefaultRow?: () => TableViewRow;
  columnVisibility?: VisibilityState;

  dataCy?: string;
  className?: string;
  emptyIcon?: any;
  emptyMessage?: string;
}

const TableView = forwardRef((props: ComponentProps, ref) => {
  const {
    id,
    data,
    columns,
    actions = {},
    isLoading = false,
    allowSort = false,
    allowPagination = false,
    manualSort = false,
    initialSortState = [],
    onSort,
    onLoadMore,
    showHeader = true,
    showCount = false,
    totalCount = 0,
    getRowId,
    getRowById,
    getDefaultRow,
    columnVisibility: parsedVisibility = {},
    className,
    dataCy = '',
    emptyIcon,
    emptyMessage = '',
  } = props;

  const [sorting, setSorting] = useState<SortingState>(initialSortState);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [validationResult, setValidationResult] = useState<ValidationResultMap>({});

  const {
    editRow,
    selectedRow,
    newRows,
    rowBackup,
    startEditRow,
    cancelEdit,
    selectRow,
    updateNewRow,
    deleteNewRow,
    setNewRows,
  } = useTableViewState();

  const {
    canAdd,
    canEdit,
    canCopy,
    canDelete,
    canSelect,
    onAddRow,
    onSelectRow,
    onUpdateRow,
    onDeleteRow,
    onCopyRow,
    onCellChange,
  } = actions;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    columns.reduce((acc, col) => ({ ...acc, [col.id as string]: col.enableHiding ? false : true }), {}),
  );

  useEffect(() => {
    if (manualSort && sorting !== initialSortState) onSort?.(sorting);
  }, [sorting]);

  const prevParsedVisibility = useRef<VisibilityState>({});
  useEffect(() => {
    if (!isEqual(prevParsedVisibility.current, parsedVisibility)) {
      setColumnVisibility((prev) => ({ ...prev, ...parsedVisibility }));
      prevParsedVisibility.current = parsedVisibility;
    }
  }, [parsedVisibility]);

  const table = useReactTable({
    data,
    columns,
    meta: { tableId: id, onCellChange: actions.onCellChange, onUpdateRow: actions.onUpdateRow },
    columnResizeMode: 'onChange',
    state: { pagination, sorting, columnVisibility },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    enableSorting: allowSort,
  });

  const rowCount = table.getRowCount() + newRows.length;

  const baseCols = table.getVisibleFlatColumns().length;
  const selectCol = canSelect ? 1 : 0;
  const editCol = canEdit ? 1 : 0;
  const colCount = baseCols + selectCol + editCol;

  const renderedRowIds = new Set(table.getRowModel().rows.map(({ id }) => id));

  // validation handlers
  const runValidation = (row: TableViewRow) =>
    validateRowAndSetResult(row, columns, setValidationResult, { readyToValidate: true });

  // internal
  const validateRow = (row: TableViewRow): boolean => {
    const latest = getRowById?.(row.id) ?? row;
    return runValidation(latest);
  };

  // trigger externally via ref
  useImperativeHandle(ref, () => ({
    validateRowExternally: (rowId: string) => {
      const row = getRowById?.(rowId) ?? data.find((r) => r.id === rowId);
      return row ? runValidation(row) : false;
    },
  }));

  const setIfValid = (callback?: (validRow: TableViewRow) => void): boolean =>
    setIfValidRow(editRow, columns, setValidationResult, setNewRows, cancelEdit, callback);

  // Internal handler functions
  const { handleAdd, handleCopy, handleDelete } = useTableHandlers({
    data,
    newRows,
    setNewRows,
    onStartEdit: startEditRow,
    onAddRow,
    onCopyRow,
    onDeleteRow,
    getDefaultRow,
    id,
  });

  const tableActions: TableActions & InternalActions = {
    canEdit,
    canCopy,
    canDelete,
    canSelect,
    onCellChange: (row, column) => {
      setIfValid(() => onCellChange?.(row, column));
    },

    onStartEdit: startEditRow,
    onCancelEdit: () => {
      if (editRow) {
        actions.onCancelEdit?.(rowBackup ?? editRow);
      }
      cancelEdit();
    },
    onCopy: handleCopy,
    onDelete: handleDelete,

    onSelectRow: (row) => {
      selectRow(row);
      onSelectRow?.(selectedRow?.id === row?.id ? undefined : row);
    },

    onDeleteRow: (row) => {
      deleteNewRow(row);
      onDeleteRow?.(row, id);
    },
    onSave: () => {
      if (editRow) {
        // Prefer the latest version from newRows (if it's a new row)
        const currentRow = getRowById?.(editRow.id) ?? newRows.find(({ id }) => id === editRow.id) ?? editRow;

        if (runValidation(currentRow)) {
          onUpdateRow?.(currentRow);
          setNewRows([]);
          cancelEdit();
        }
      }
    },
  };

  const showPagination = rowCount > 0 && allowPagination && rowCount < totalCount;
  const tableClassName = classNames('table-view-component', className, { 'fixed-table': canEdit || canDelete });

  return (
    <>
      {showCount && !isLoading && (
        <div className="row-counter">
          Showing {rowCount} {totalCount > 0 ? `of ${totalCount} total` : ''} rows
        </div>
      )}

      <ReactstrapTable
        data-id={id}
        className={tableClassName}
        data-cy={dataCy}
        striped={rowCount > 0}
        responsive
        size="sm"
      >
        {showHeader && (
          <TableHeader headerGroups={table.getHeaderGroups()} allowSort={allowSort} tableActions={tableActions} />
        )}

        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={colCount}>
                <Spinner loading className="spinner-container" />
              </td>
            </tr>
          )}

          {!isLoading && rowCount === 0 && <TableEmpty colSpan={colCount} icon={emptyIcon} message={emptyMessage} />}

          {table.getRowModel().rows.map((row) => {
            const { id, original } = row;
            const { isCustom = false, getCustomRow } = original;

            if (isCustom && getCustomRow) {
              return getCustomRow({ id, colCount, rowCount });
            }

            return (
              <TableRow
                key={id}
                row={row}
                selected={selectedRow?.id === id}
                isEditing={editRow?.id === id}
                editRow={editRow}
                onCellChange={(row, column) => {
                  const columnId = column.id;
                  if (!columnId) return;

                  const value = row[columnId];
                  onCellChange?.(row.id, columnId, value);
                  validateRow(row);
                }}
                rowActions={{
                  ...tableActions,
                  canEdit: resolveRowAction(canEdit, original),
                  canDelete: resolveRowAction(canDelete, original),
                  canCopy: resolveRowAction(canCopy, original),
                }}
                validateRow={validateRow}
                validationResults={validationResult[id]}
              />
            );
          })}
          {newRows
            .filter((row) => !renderedRowIds.has(row.id as string))
            .map((row) => {
              return (
                <TableRowNew
                  key={row.id}
                  row={row}
                  columns={columns}
                  onChange={(row, column, value) => {
                    const columnId = column?.id;
                    if (!columnId) return;

                    const freshRow = newRows.find((r) => r.id === row.id) ?? row;
                    const current = freshRow[columnId];

                    if (current !== value) {
                      updateNewRow(row.id, columnId, value);
                      onCellChange?.(row.id, columnId, value);
                    }
                  }}
                  onSave={tableActions.onSave}
                  onCancel={cancelEdit}
                  canSelect={canSelect}
                  colCount={colCount}
                  validateRow={validateRow}
                  validationResults={validationResult[row.id]}
                />
              );
            })}
        </tbody>
        <TableFooter
          id={id}
          canAdd={canAdd}
          editRow={editRow}
          onAddRow={handleAdd}
          showPagination={showPagination}
          onLoadMore={onLoadMore}
          colCount={colCount}
        />
      </ReactstrapTable>
    </>
  );
});

TableView.displayName = 'TableView';

export default TableView;
